import { useMemo, useState, useEffect, useRef } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import {
  getListViewSchema,
  hasListViewSchema
} from "@/lib/views/listViewSchemas";
import {
  getActiveListViewConfig,
  buildColumnsFromListViewConfig,
  convertListViewSortToTanStackSort,
} from "@/lib/views/utils/buildListColumnsFromSchema";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { meta } from "@/lib/erp/meta";
import type { DocTypeSchema, DocFieldSchema } from "@/lib/erp/meta";
import type { ListSettingsConfig, ListSettingsFieldConfig } from "@/lib/hooks/listSettingsTypes";
import { formatDate, formatDateTime } from "@/lib/format";
import { useLanguage } from "@/i18n/useLanguage";

type UseStandardListConfigParams = {
  doctype: string;
  filterStoreKey: string;
};

type StandardListConfig = {
  columns: (ctx?: any) => ColumnDef<any, any>[];
  defaultSorting: Array<{ id: string; desc: boolean }>;
  fields: string[];
  quickFilters?: any[];
  storefrontFilterField?: string;
  listSettingsConfig?: ListSettingsConfig;
  /** Synthetic columns offered ONLY in the export dialog (not on-screen). */
  exportExtraFields?: ListSettingsFieldConfig[];
  isLoading: boolean;
};

// Minimal columns constant (never empty array)
const MIN_COLS: ColumnDef<any, any>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => DataTableColumnHeader({ column, label: "Name" }),
    enableSorting: true,
  },
  {
    accessorKey: "modified",
    header: ({ column }) => DataTableColumnHeader({ column, label: "Modified" }),
    enableSorting: true,
  },
];

// Build column from field definition — uses DataTableColumnHeader for consistent rendering
function buildColumnFromField(field: DocFieldSchema): ColumnDef<any, any> {
  const label = field.label || field.fieldname;
  return {
    accessorKey: field.fieldname,
    header: ({ column }) => DataTableColumnHeader({ column, label }),
    enableSorting: true,
    cell: ({ row }) => {
      const value = row.getValue(field.fieldname);
      if (value === null || value === undefined) return "-";

      if (field.fieldtype === "Link" && field.link_title_field) {
        // Frappe returns dot-notation fields with the leaf key only
        // e.g. "company.tenant_display_name" → response key is "tenant_display_name"
        const titleValue =
          row.original[field.link_title_field] ??
          row.original[`${field.fieldname}.${field.link_title_field}`];
        if (titleValue) return String(titleValue);
      }
      if (field.fieldtype === "Date") {
        return formatDate(value as string | number | Date) || String(value);
      }
      if (field.fieldtype === "Datetime") {
        return formatDateTime(value as string | number | Date) || String(value);
      }
      if (field.fieldtype === "Currency" || field.fieldtype === "Float") {
        return typeof value === "number" ? value.toFixed(2) : value;
      }
      if (field.fieldtype === "Check") {
        return value ? "Yes" : "No";
      }
      return String(value);
    },
  };
}

// Build columns from meta with improved heuristic
function buildColumnsFromMeta(meta: DocTypeSchema): ColumnDef<any, any>[] {
  const selectedFields = new Set<string>(["name"]);
  
  // Priority 1: title_field
  if (meta.title_field) {
    selectedFields.add(meta.title_field);
  }
  
  // Priority 2-5: Common fields
  const priorityFields = [
    ["status", "workflow_state", "docstatus"],
    ["customer", "supplier", "party"],
    ["posting_date", "transaction_date", "date"],
    ["grand_total", "total", "amount"],
  ];
  
  priorityFields.forEach((fieldGroup) => {
    fieldGroup.forEach((fieldname) => {
      if (meta.fields.some((f) => f.fieldname === fieldname && !f.hidden)) {
        selectedFields.add(fieldname);
      }
    });
  });
  
  // Priority 6: in_list_view fields (fill remaining slots)
  const inListViewFields = meta.fields
    .filter((f) => f.in_list_view === 1 && !f.hidden && !selectedFields.has(f.fieldname))
    .slice(0, 8 - selectedFields.size);
  
  inListViewFields.forEach((f) => selectedFields.add(f.fieldname));
  selectedFields.add("modified");
  
  // Build columns
  return Array.from(selectedFields).map((fieldname) => {
    // System fields (name, modified) aren't in meta.fields — provide correct fallbacks
    const field = meta.fields.find((f) => f.fieldname === fieldname) || {
      fieldname,
      label: fieldname === "name" ? "Name" : fieldname === "modified" ? "Modified" : fieldname,
      fieldtype: fieldname === "modified" ? "Datetime" as const : "Data" as const,
    };

    // For the "name" column: if the doctype has a title_field, display that instead
    if (fieldname === "name" && meta.title_field) {
      const titleFieldname = meta.title_field;
      return {
        accessorKey: "name",
        header: ({ column }: { column: any }) => DataTableColumnHeader({ column, label: "Name" }),
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const titleValue = row.original[titleFieldname];
          return titleValue ? String(titleValue) : String(row.getValue("name") ?? "-");
        },
      } as ColumnDef<any, any>;
    }

    return buildColumnFromField(field);
  });
}

export function useStandardListConfig({ 
  doctype, 
  filterStoreKey 
}: UseStandardListConfigParams): StandardListConfig {
  const { language } = useLanguage();
  const [metaColumns, setMetaColumns] = useState<ColumnDef<any, any>[] | null>(null);
  const [metaFields, setMetaFields] = useState<string[]>([]);
  const [metaSchema, setMetaSchema] = useState<DocTypeSchema | null>(null);
  const [isLoadingMeta, setIsLoadingMeta] = useState(false);

  // Synthetic export-ONLY columns — surfaced only in the export column picker,
  // never in the on-screen column menu (the on-screen list fetch has no meta
  // sanitization, so a non-field here would 417 the list). The backend
  // list_export engine computes these (see COMPUTED_COLUMNS in list_export.py).
  // TKT-41: Sales Order gets a computed Payment Status (Paid/Partial/Unpaid).
  const exportExtraFields = useMemo<ListSettingsFieldConfig[]>(() => {
    if (doctype === "Sales Order") {
      return [
        {
          fieldname: "payment_status",
          label: language === "th" ? "สถานะการชำระเงิน" : "Payment Status",
          fieldtype: "Data",
          defaultVisible: false,
        },
      ];
    }
    return [];
  }, [doctype, language]);

  // Reset stale meta state when doctype changes (OPS-1577).
  // Without this, navigating between doctypes reuses the previous
  // doctype's enriched fields (e.g. "company.tenant_display_name")
  // which crashes Frappe's reportview for doctypes with different schemas.
  const prevDoctypeRef = useRef(doctype);
  useEffect(() => {
    if (prevDoctypeRef.current !== doctype) {
      prevDoctypeRef.current = doctype;
      setMetaColumns(null);
      setMetaFields([]);
      setMetaSchema(null);
    }
  }, [doctype]);

  // Try schema first
  const hasSchema = hasListViewSchema(doctype);
  const schema = hasSchema ? getListViewSchema(doctype) : null;
  const viewConfig = schema 
    ? getActiveListViewConfig(schema, "experience") 
    : null;
  
  // Build columns from schema if available
  const schemaColumns = useMemo(() => {
    if (!viewConfig) return null;
    return buildColumnsFromListViewConfig<any>(viewConfig);
  }, [viewConfig]);
  
  // Fetch meta if no schema
  useEffect(() => {
    if (hasSchema || metaColumns !== null || isLoadingMeta) return;
    
    setIsLoadingMeta(true);
    meta.getDocTypeSchema(doctype)
      .then((docSchema) => {
        const columns = buildColumnsFromMeta(docSchema);
  // @ts-expect-error - Property may not exist on type
        const fieldnames: string[] = columns.map((col) => col.accessorKey as string);

        // Pre-compute dot-notation title fields for Link columns here
        // so the fields array is complete in a single state update.
        // Previously this was done in the fields memo depending on metaSchema,
        // but React could render with metaFields set while metaSchema was still null.
        const enrichedFields = [...fieldnames];

        // Include title_field in the request so the name column can display it
        if (docSchema.title_field && !enrichedFields.includes(docSchema.title_field)) {
          enrichedFields.push(docSchema.title_field);
        }

        for (const fn of fieldnames) {
          const field = docSchema.fields.find((f) => f.fieldname === fn);
          if (field?.link_title_field) {
            enrichedFields.push(`${fn}.${field.link_title_field}`);
          }
        }

        setMetaColumns(columns);
        setMetaFields(enrichedFields);
        setMetaSchema(docSchema);
      })
      .catch((err) => {
        // ✅ Fix: Set minimal columns, not empty array
        console.warn(`[useStandardListConfig] Failed to fetch meta for ${doctype}:`, err);
        setMetaColumns(MIN_COLS);
        setMetaFields(["name", "modified"]);
      })
      .finally(() => setIsLoadingMeta(false));
  }, [doctype, hasSchema, metaColumns, isLoadingMeta]);
  
  // ✅ Fix: Safe length checks, always return function
  const columns = useMemo(() => {
    if (schemaColumns?.length) return () => schemaColumns;
    if (metaColumns?.length) return () => metaColumns;
    return () => MIN_COLS; // ✅ Reuse constant
  }, [schemaColumns, metaColumns]);
  
  // Default sorting
  const defaultSorting = useMemo(() => {
    if (viewConfig?.defaultSort) {
      return convertListViewSortToTanStackSort(viewConfig.defaultSort);
    }
    return [{ id: "modified", desc: true }];
  }, [viewConfig]);
  
  // Fields: reactive to active column set
  // For Link fields with link_title_field, also request the dot-notation
  // title field (e.g. "company.tenant_display_name") so Frappe JOINs and
  // returns the clean display name alongside the raw Link value.
  const fields = useMemo(() => {
    const baseFields = ["name", "modified"];

    if (schemaColumns && viewConfig?.columns) {
      const schemaFieldnames = viewConfig.columns
        .map((col) => col.fieldname)
        .filter(Boolean);
      const extraFields = viewConfig.additionalFields || [];
      return Array.from(new Set([...baseFields, ...schemaFieldnames, ...extraFields]));
    }

    if (metaFields.length > 0) {
      // metaFields already includes dot-notation title fields (enriched at setState time)
      return Array.from(new Set([...baseFields, ...metaFields]));
    }

    return baseFields;
  }, [schemaColumns, viewConfig, metaFields, metaSchema]);
  
  // Quick filters from schema
  const quickFilters = viewConfig?.quickFilters;

  // Storefront-attribution field (opt-in per schema) — drives per-store tabs
  const storefrontFilterField = viewConfig?.storefrontFilterField;
  
  // List settings config
  const listSettingsConfig = useMemo(() => {
    if (viewConfig) {
      // Schema-driven: use schema's listSettingsConfig pattern
      return {
        key: filterStoreKey,
        meta: {
          doctype,
          includeFieldtypes: [
            "Data", "Currency", "Date", "Datetime", "Check", "Int", "Float",
            "Link", "Select", "Small Text", "Text", "Long Text",
          ],
          excludeFieldtypes: [
            "Section Break", "Column Break", "HTML", "Table", "Table MultiSelect",
            "Button", "Heading",
          ],
          excludeFieldnames: [
            "naming_series", "owner", "creation", "modified", "modified_by",
            "idx", "__islocal", "__unsaved", "amended_from", "amended_date",
            "_assign", "_comment_count", "_liked_by",
          ],
          respectInListView: true,
          // Schema default: use schema columns as initial visible order
          defaultVisibleFieldnames: viewConfig.columns?.map((col) => col.fieldname) || [],
        },
      };
    }
    
    // Meta-driven: build from fetched meta
    // Include title_field + Link dot-notation title fields as required so the Shell
    // always fetches them even when user customizes visible columns.
    const requiredFieldnames: string[] = [];
    if (metaSchema?.title_field) {
      requiredFieldnames.push(metaSchema.title_field);
    }
    if (metaFields.length > 0 && metaSchema) {
      for (const fn of metaFields) {
        // Dot-notation fields like "company.tenant_display_name"
        if (fn.includes(".")) {
          requiredFieldnames.push(fn);
        }
      }
    }

    return {
      key: filterStoreKey,
      meta: {
        doctype,
        includeFieldtypes: [
          "Data", "Currency", "Date", "Datetime", "Check", "Int", "Float",
          "Link", "Select", "Small Text", "Text",
        ],
        excludeFieldtypes: [
          "Section Break", "Column Break", "HTML", "Table", "Button", "Heading",
        ],
        excludeFieldnames: [
          "naming_series", "owner", "creation", "modified", "modified_by",
          "idx", "__islocal", "__unsaved",
        ],
        ...(requiredFieldnames.length > 0 ? { requiredFieldnames } : {}),
      },
    };
  }, [viewConfig, doctype, filterStoreKey, metaSchema, metaFields]);
  
  return {
    columns,
    defaultSorting,
    fields,
    quickFilters,
    storefrontFilterField,
    listSettingsConfig,
    exportExtraFields,
    isLoading: isLoadingMeta && !hasSchema,
  };
}


